"use client";

import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/components/ui/cn";
import { BELOW_SM, usePageOverlay } from "@/hooks/usePageOverlay";

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Optional group heading rendered above consecutive options that share it. */
  group?: string;
  /** Extra text the search matches without showing it (e.g. the other
   *  language's name, codes). */
  keywords?: string;
}

export interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  /** Shown as the trigger label when nothing is selected. */
  placeholder?: string;
  /** Placeholder for the local-search input inside the popover. */
  searchPlaceholder?: string;
  /** When set, the search term is also reported to the parent (debounced),
   *  so it can widen the option list with a backend query. The parent is
   *  then the source of truth for what matches, so the in-place label
   *  filter is skipped. Cleared to "" when the popover closes, unless
   *  `retainSearchTerm` is set. */
  onSearch?: (term: string) => void;
  /** Keep the search term (and keep reporting it via `onSearch`) after the
   *  popover closes, instead of resetting to "". Use when the same term also
   *  drives content outside the dropdown (e.g. a list below it): resetting on
   *  close would yank that content out from under a click that is landing on
   *  it. A clear (×) button in the search field lets the user reset it. */
  retainSearchTerm?: boolean;
  /** With `onSearch`: a backend query for the current term is in flight.
   *  Suppresses the "no results" message so the list doesn't flash empty
   *  before the widened options arrive. */
  loading?: boolean;
  /** Shown inside the popover while `loading`. */
  loadingMessage?: string;
  /** Shown inside the popover when a search matches no options. */
  noResultsMessage?: string;
  /** Shown in place of the trigger when there are no options to pick from. */
  emptyMessage?: string;
  /** Custom trigger content for the selected option (defaults to its label). */
  renderSelected?: (option: SearchableSelectOption) => ReactNode;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  className?: string;
  id?: string;
  /** Show option `group`s as headings (default). `false` renders a plain
   *  list in the given order; `group` is then only searched. */
  grouped?: boolean;
}

/**
 * Select-like dropdown styled after the locale switcher's popover menu, but
 * for lists too long to scan by eye: typing filters the options in place
 * (client-side, no network round-trip) instead of relying on native <select>
 * type-ahead. Drop-in replacement for the plain `<Select>` wherever the
 * option list can grow large (foreign keys, package types, locations...).
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  onSearch,
  retainSearchTerm,
  loading,
  loadingMessage,
  noResultsMessage,
  emptyMessage,
  renderSelected,
  disabled,
  invalid,
  className,
  id,
  grouped = true,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  // iOS only raises the keyboard for focus() inside the tap handler, but the
  // real input mounts a render later. Focus an invisible stand-in during the
  // tap; focus then moves to the real input and the keyboard stays up.
  const keyboardProxyRef = useRef<HTMLInputElement | null>(null);
  // The popover is portaled to <body> so an ancestor with `overflow` (modal
  // body, scrollable table wrapper) can't clip it; this tracks the trigger's
  // viewport rect so the fixed-position popover can follow it.
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    // With `onSearch` the parent has already narrowed `options` to the
    // backend matches for this term — filtering again by label here would
    // drop matches on fields the label doesn't show (e.g. tracking number).
    if (onSearch) return options;
    // Every word must occur somewhere — "pallet wood" finds "Паллета дерево"
    // through its English keywords.
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return options;
    return options.filter((o) => {
      const text = `${o.label} ${o.group ?? ""} ${o.keywords ?? ""}`.toLowerCase();
      return words.every((w) => text.includes(w));
    });
  }, [options, search, onSearch]);


  // Focus the search box once the portaled popover has actually mounted
  // (it waits on `anchorRect`), then drop the iOS keyboard placeholder.
  const popoverMounted = open && anchorRect !== null;
  useEffect(() => {
    if (!popoverMounted) return;
    searchRef.current?.focus({ preventScroll: true });
    keyboardProxyRef.current?.remove();
    keyboardProxyRef.current = null;
  }, [popoverMounted]);
  useEffect(() => () => keyboardProxyRef.current?.remove(), []);

  // Keep the portaled popover pinned to the trigger while open.
  useEffect(() => {
    if (!open) return;
    const update = () => {
      const el = rootRef.current;
      if (el) setAnchorRect(el.getBoundingClientRect());
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  // A selection made elsewhere (e.g. a row in a list this same term filters)
  // ends the search — don't leave a stale term behind under `retainSearchTerm`.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSearch("");
  }, [value]);

  // Report the search term upward (debounced) so the parent can widen the
  // options with a backend query; reset it when the popover is closed.
  useEffect(() => {
    if (!onSearch) return;
    if (!open) {
      if (!retainSearchTerm) onSearch("");
      return;
    }
    const id = setTimeout(() => onSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [open, search, onSearch, retainSearchTerm]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      const target = e.target as Node;
      if (
        rootRef.current &&
        !rootRef.current.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // A backend-backed select (`onSearch`) must stay open and interactive even
  // when the current term matches nothing — otherwise the popover unmounts
  // mid-search, the trigger goes disabled, and the page looks frozen until
  // the user clicks elsewhere to clear the stale `open` state.
  const noOptions = options.length === 0 && !onSearch;

  // Phones: the full-screen sheet is `absolute`, not `fixed` (iOS toolbar
  // tint, see usePageOverlay); sm+: a fixed dropdown under the trigger.
  // Keyed on the popover actually being in the DOM (it waits on
  // `anchorRect`), so the hook finds the element when it runs.
  usePageOverlay(popoverRef, popoverMounted && !noOptions, { media: BELOW_SM });

  function toggle() {
    if (!open) {
      const proxy = document.createElement("input");
      proxy.setAttribute("aria-hidden", "true");
      proxy.tabIndex = -1;
      proxy.style.cssText =
        "position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;font-size:16px;pointer-events:none;";
      document.body.appendChild(proxy);
      proxy.focus({ preventScroll: true });
      keyboardProxyRef.current = proxy;
    }
    setOpen((o) => {
      if (!o && !retainSearchTerm) setSearch("");
      return !o;
    });
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        id={id}
        type="button"
        disabled={disabled || noOptions}
        onClick={toggle}
        className={cn(
          "flex h-10 w-full items-center justify-between gap-2 rounded-lg border bg-surface px-3 text-left text-sm text-foreground",
          "transition-colors focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]/30",
          invalid ? "border-danger" : "border-border",
          open && "border-accent/50 ring-2 ring-[color:var(--ring)]/30",
          (disabled || noOptions) && "cursor-not-allowed opacity-60",
          className
        )}
      >
        <span className={cn("min-w-0 truncate", !selected && "text-muted/60")}>
          {noOptions
            ? emptyMessage ?? ""
            : selected
              ? renderSelected
                ? renderSelected(selected)
                : selected.label
              : placeholder ?? ""}
        </span>
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn("shrink-0 text-muted transition-transform", open && "rotate-180")}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && !noOptions && anchorRect && typeof document !== "undefined" &&
        createPortal(
        // `data-enter-native`: the popover's search box owns Enter (it
        // filters), so it must stay out of the form-wide "Enter moves to
        // the next field" order — see lib/formNavigation.ts.
        <div
          ref={popoverRef}
          data-enter-native
          // Phones (< sm): full-screen sheet; `data-picker-sheet` also
          // locks page scroll from globals.css. sm+: dropdown pinned under
          // the trigger via the CSS vars below.
          data-picker-sheet
          style={
            {
              "--picker-top": `${anchorRect.bottom + 6}px`,
              "--picker-left": `${anchorRect.left}px`,
              "--picker-width": `${anchorRect.width}px`,
            } as CSSProperties
          }
          className={cn(
            "absolute inset-x-0 top-0 z-50 flex h-dvh flex-col bg-surface pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]",
            "sm:fixed sm:inset-auto sm:top-(--picker-top) sm:left-(--picker-left) sm:block sm:h-auto sm:w-(--picker-width) sm:min-w-[12rem] sm:overflow-hidden sm:rounded-lg sm:border sm:border-border sm:p-0 sm:shadow-lg"
          )}
        >
          <div className="flex shrink-0 items-center gap-2 border-b border-border p-2 sm:p-1.5">
          <div className="relative min-w-0 flex-1">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className={cn(
                "h-11 py-2 sm:h-auto sm:py-1.5",
                "w-full rounded-md border border-border bg-surface pl-2.5 text-base text-foreground placeholder:text-muted/60 focus:border-accent focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]/30",
                search ? "pr-11 sm:pr-8" : "pr-2.5"
              )}
            />
            {search && (
              <button
                type="button"
                aria-label="Clear"
                onClick={() => {
                  setSearch("");
                  onSearch?.("");
                  searchRef.current?.focus();
                }}
                className={cn(
                  "absolute top-1/2 flex -translate-y-1/2 items-center justify-center rounded text-muted hover:bg-surface-muted hover:text-foreground",
                  "right-1 h-9 w-9 sm:right-2.5 sm:h-6 sm:w-6"
                )}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
            {/* Sheet close — phones only; on sm+ outside-click closes it. */}
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(false)}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted transition-colors hover:bg-surface-muted hover:text-foreground active:bg-surface-muted sm:hidden"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div
            role="listbox"
            className={cn(
              "overflow-y-auto overscroll-contain py-1",
              "min-h-0 flex-1 sm:max-h-56 sm:flex-none"
            )}
          >
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted">
                {loading ? loadingMessage ?? "…" : noResultsMessage}
              </p>
            ) : (
              filtered.map((o, i) => {
                const isSelected = o.value === value;
                const showGroupHeading =
                  grouped && o.group && o.group !== filtered[i - 1]?.group;
                return (
                  <div key={o.value}>
                    {showGroupHeading && (
                      <p className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted/70">
                        {o.group}
                      </p>
                    )}
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => {
                        onChange(o.value);
                        // A pick completes the search: clear it and tell the
                        // parent, even under `retainSearchTerm`.
                        setSearch("");
                        onSearch?.("");
                        setOpen(false);
                      }}
                      className={cn(
                        "min-h-12 px-4 py-3 text-base sm:min-h-0 sm:px-3 sm:py-2 sm:text-sm",
                        "flex w-full items-center justify-between gap-3 text-left transition-colors hover:bg-surface-muted",
                        isSelected ? "font-medium text-accent" : "text-foreground"
                      )}
                    >
                      <span className="truncate">{o.label}</span>
                      {isSelected && (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="shrink-0"
                        >
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      )}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
